/**
 * Storage Router Service
 *
 * Routes data operations to the correct storage backend based on user's plan:
 * - Free/Premium → Firestore (existing ProfileService)
 * - Pro → Google Drive (GoogleDriveRepository)
 *
 * This service acts as a facade that ProfileContext can use without knowing
 * which storage backend is being used.
 */

import type { Address, Vehicle, InsurancePolicy } from '../types/profile';
import type { HealthProvider } from '../types/health';
import type { FinancialInstitution } from '../types/financial';
import { ProfileService } from './profileService';
import { auth } from '../config/firebase';
import { googleDriveRepository } from './repository/GoogleDriveRepository';
import { googleDriveService } from './googleDriveService';
import { usesGoogleDrive } from '../constants/planLimits';
import type { SubscriptionPlan } from '../constants/planLimits';

/**
 * Get the user's plan from pilotInfo stored in localStorage or Firestore
 */
async function getCurrentUserPlan(): Promise<SubscriptionPlan> {
  const userId = ProfileService.getCurrentUserId();
  if (!userId) return 'free';

  // Check both cache keys — usePlanLimits writes 'lucidDock_cachedPlan',
  // storageRouter writes 'lucidDock_membershipPlan'
  const validPlans: SubscriptionPlan[] = ['free', 'pro', 'premium'];
  for (const key of ['lucidDock_membershipPlan', 'lucidDock_cachedPlan']) {
    const cached = localStorage.getItem(key);
    if (cached && validPlans.includes(cached as SubscriptionPlan)) {
      return cached as SubscriptionPlan;
    }
  }

  const currentUser = auth.currentUser;
  // Only the pilot can read their own pilot doc
  if (!currentUser || currentUser.uid !== userId) {
    return 'free';
  }

  try {
    const pilotInfo = await ProfileService.getPilotInfo(userId);
    if (pilotInfo?.membershipPlan) {
      if (validPlans.includes(pilotInfo.membershipPlan as SubscriptionPlan)) {
        localStorage.setItem('lucidDock_membershipPlan', pilotInfo.membershipPlan);
        return pilotInfo.membershipPlan as SubscriptionPlan;
      }
    }
  } catch (error) {
    console.error('StorageRouter: Error getting user plan:', error);
  }

  return 'free';
}

/**
 * Check if current user should use Google Drive storage.
 *
 * Pro users can choose between Cloud and Google Drive. The preference is stored
 * in localStorage as 'lucidDock_storageProvider' ('firebase' | 'google-drive').
 * Defaults to 'firebase' (Cloud) if no preference is set.
 *
 * Family members inherit the owner's storage choice via the family Drive context
 * stored in localStorage during ProfileContext initialization.
 */
async function shouldUseGoogleDrive(): Promise<boolean> {
  const userType = localStorage.getItem('lucidDock_userType');

  if (userType === 'family') {
    const rootFolderId = localStorage.getItem('lucidDock_familyDriveRootFolderId');
    const ownerEmail = localStorage.getItem('lucidDock_familyDriveOwnerEmail');
    return !!(rootFolderId && ownerEmail);
  }

  const plan = await getCurrentUserPlan();
  if (!usesGoogleDrive(plan)) return false;

  // Pro plan defaults to Google Drive unless explicitly switched to Firebase via settings.
  // 'firebase' is only set by the "Switch to Cloud" button in StorageSettingsModal.
  const preference = localStorage.getItem('lucidDock_storageProvider');
  return preference !== 'firebase' && googleDriveService.isConnected();
}

/**
 * Initialize Google Drive repository if needed.
 *
 * CRITICAL: Family members must call initializeAsFamily() which accesses the
 * Pilot's shared Drive folder. Calling initialize() would create a LucidDock
 * folder in the family member's own Drive — a serious data isolation bug.
 */
async function ensureGoogleDriveInitialized(profileId: string, profileName: string): Promise<boolean> {
  // Import googleDriveService to check connection and get valid token
  const { googleDriveService } = await import('./googleDriveService');

  // First, check if Google Drive is connected at all
  if (!googleDriveService.isConnected()) {
    console.error('StorageRouter: Google Drive not connected. Please connect Google Drive first.');
    return false;
  }

  // Get a valid token (this handles refresh if needed)
  const validToken = await googleDriveService.getValidAccessToken();
  if (!validToken) {
    console.error('StorageRouter: Failed to get valid Google Drive token. Please reconnect Google Drive.');
    return false;
  }

  if (!googleDriveRepository.isReady()) {
    try {
      const userType = localStorage.getItem('lucidDock_userType');

      if (userType === 'family') {
        // Family member: access Pilot's shared Drive folder — NEVER create folders
        const rootFolderId = localStorage.getItem('lucidDock_familyDriveRootFolderId');
        const ownerEmail = localStorage.getItem('lucidDock_familyDriveOwnerEmail');

        if (!rootFolderId || !ownerEmail) {
          console.error('StorageRouter: Missing family Drive context. Pilot root folder ID or email not cached.');
          return false;
        }

        await googleDriveRepository.initializeAsFamily(profileId, profileName, rootFolderId, ownerEmail);
      } else {
        // Pilot: normal initialization (creates folders if needed)
        await googleDriveRepository.initialize(profileId, profileName);
      }

      const ready = googleDriveRepository.isReady();
      if (!ready) {
        console.error('StorageRouter: Google Drive repository failed to initialize. Token may have expired.');
      }
      return ready;
    } catch (error) {
      console.error('StorageRouter: Failed to initialize Google Drive:', error);
      return false;
    }
  }
  return true;
}

// ============================================================================
// ADDRESS OPERATIONS
// ============================================================================

/**
 * Add an address - routes to correct storage based on plan
 */
export async function addAddress(
  profileId: string,
  address: Omit<Address, 'id'>,
  profileName?: string
): Promise<string> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    // Pro users: Save to Google Drive
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected. Please connect your Google Drive to continue.');
    }

    // Generate ID for the address
    const id = `addr_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const addressWithId: Address = { ...address, id } as Address;

    await googleDriveRepository.saveAddress(profileId, addressWithId);
    await googleDriveRepository.forceSave();
    return id;
  } else {
    // Free/Premium users: Save to Firestore
    const id = await ProfileService.addAddress(profileId, address);
    return id;
  }
}

/**
 * Update an address
 */
export async function updateAddress(
  profileId: string,
  addressId: string,
  updates: Partial<Address>,
  currentAddress: Address,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const updatedAddress: Address = { ...currentAddress, ...updates, id: addressId };
    await googleDriveRepository.saveAddress(profileId, updatedAddress);

    // CRITICAL: Force immediate save when documents or photos are updated
    // The debounced save (1 second) could lose data if user navigates away
    if (updates.documents || updates.photos) {
      await googleDriveRepository.forceSave();
    }
  } else {
    await ProfileService.updateAddress(profileId, addressId, updates);
  }
}

/**
 * Delete an address
 */
export async function deleteAddress(
  profileId: string,
  addressId: string,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (initialized) {
      await googleDriveRepository.deleteAddress(profileId, addressId);
    }
  } else {
    await ProfileService.deleteAddress(profileId, addressId);
  }
}

// ============================================================================
// VEHICLE OPERATIONS
// ============================================================================

export async function addVehicle(
  profileId: string,
  vehicle: Omit<Vehicle, 'id'>,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const id = `veh_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const vehicleWithId: Vehicle = { ...vehicle, id } as Vehicle;
    await googleDriveRepository.saveVehicle(profileId, vehicleWithId);
  } else {
    await ProfileService.addVehicle(profileId, vehicle);
  }
}

export async function updateVehicle(
  profileId: string,
  vehicleId: string,
  updates: Partial<Vehicle>,
  currentVehicle: Vehicle,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const updatedVehicle: Vehicle = { ...currentVehicle, ...updates, id: vehicleId };
    await googleDriveRepository.saveVehicle(profileId, updatedVehicle);

    // CRITICAL: Force immediate save when documents or photos are updated
    if (updates.documents || updates.photos) {
      await googleDriveRepository.forceSave();
    }
  } else {
    await ProfileService.updateVehicle(profileId, vehicleId, updates);
  }
}

export async function deleteVehicle(
  profileId: string,
  vehicleId: string,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (initialized) {
      await googleDriveRepository.deleteVehicle(profileId, vehicleId);
    }
  } else {
    await ProfileService.deleteVehicle(profileId, vehicleId);
  }
}

// ============================================================================
// INSURANCE OPERATIONS
// ============================================================================

export async function addInsurancePolicy(
  profileId: string,
  policy: Omit<InsurancePolicy, 'id'>,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const id = `ins_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const policyWithId: InsurancePolicy = { ...policy, id } as InsurancePolicy;
    await googleDriveRepository.saveInsurancePolicy(profileId, policyWithId);
  } else {
    await ProfileService.addInsurancePolicy(profileId, policy);
  }
}

export async function updateInsurancePolicy(
  profileId: string,
  policyId: string,
  updates: Partial<InsurancePolicy>,
  currentPolicy: InsurancePolicy,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const updatedPolicy: InsurancePolicy = { ...currentPolicy, ...updates, id: policyId };
    await googleDriveRepository.saveInsurancePolicy(profileId, updatedPolicy);

    // CRITICAL: Force immediate save when documents or photos are updated
    if (updates.documents || updates.photos) {
      await googleDriveRepository.forceSave();
    }
  } else {
    await ProfileService.updateInsurancePolicy(profileId, policyId, updates);
  }
}

export async function deleteInsurancePolicy(
  profileId: string,
  policyId: string,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (initialized) {
      await googleDriveRepository.deleteInsurancePolicy(profileId, policyId);
    }
  } else {
    await ProfileService.deleteInsurancePolicy(profileId, policyId);
  }
}

// ============================================================================
// HEALTH PROVIDER OPERATIONS
// ============================================================================

export async function addHealthProvider(
  profileId: string,
  provider: Omit<HealthProvider, 'id'>,
  profileName?: string
): Promise<string> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const id = `health_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const providerWithId: HealthProvider = { ...provider, id } as HealthProvider;
    await googleDriveRepository.saveHealthProvider(profileId, providerWithId);
    return id;
  } else {
    const id = `health_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return id;
  }
}

export async function updateHealthProvider(
  profileId: string,
  providerId: string,
  updates: Partial<HealthProvider>,
  currentProvider: HealthProvider,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const updatedProvider: HealthProvider = { ...currentProvider, ...updates, id: providerId };
    await googleDriveRepository.saveHealthProvider(profileId, updatedProvider);

    // CRITICAL: Force immediate save when documents are updated
    // Note: HealthProvider doesn't have photos
    if (updates.documents) {
      await googleDriveRepository.forceSave();
    }
  }
}

export async function deleteHealthProvider(
  profileId: string,
  providerId: string,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (initialized) {
      await googleDriveRepository.deleteHealthProvider(profileId, providerId);
    }
  }
}

// ============================================================================
// FINANCIAL INSTITUTION OPERATIONS
// ============================================================================

export async function addFinancialInstitution(
  profileId: string,
  institution: Omit<FinancialInstitution, 'id'>,
  profileName?: string
): Promise<string> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const id = `fin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const institutionWithId: FinancialInstitution = { ...institution, id } as FinancialInstitution;
    await googleDriveRepository.saveFinancialInstitution(profileId, institutionWithId);
    return id;
  } else {
    const id = `fin_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    return id;
  }
}

export async function updateFinancialInstitution(
  profileId: string,
  institutionId: string,
  updates: Partial<FinancialInstitution>,
  currentInstitution: FinancialInstitution,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (!initialized) {
      throw new Error('Google Drive not connected.');
    }

    const updatedInstitution: FinancialInstitution = { ...currentInstitution, ...updates, id: institutionId };
    await googleDriveRepository.saveFinancialInstitution(profileId, updatedInstitution);

    // CRITICAL: Force immediate save when documents are updated
    // Note: FinancialInstitution doesn't have photos
    if (updates.documents) {
      await googleDriveRepository.forceSave();
    }
  }
}

export async function deleteFinancialInstitution(
  profileId: string,
  institutionId: string,
  profileName?: string
): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();

  if (useGoogleDrive) {
    const initialized = await ensureGoogleDriveInitialized(profileId, profileName || 'Profile');
    if (initialized) {
      await googleDriveRepository.deleteFinancialInstitution(profileId, institutionId);
    }
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if user is using Google Drive storage
 */
export async function isUsingGoogleDrive(): Promise<boolean> {
  return shouldUseGoogleDrive();
}

/**
 * Sync Google Drive data (for Pro users)
 */
export async function syncGoogleDrive(profileId: string): Promise<void> {
  const useGoogleDrive = await shouldUseGoogleDrive();
  if (useGoogleDrive && googleDriveRepository.isReady()) {
    await googleDriveRepository.sync(profileId);
  }
}

/**
 * Get all addresses from the appropriate storage
 */
export async function getAddresses(profileId: string): Promise<Address[]> {
  const useGoogleDrive = await shouldUseGoogleDrive();
  if (useGoogleDrive && googleDriveRepository.isReady()) {
    return googleDriveRepository.getAddresses(profileId);
  }
  // For Firestore, data is loaded via ProfileService.initializeProfiles
  return [];
}

/**
 * Get all vehicles from the appropriate storage
 */
export async function getVehicles(profileId: string): Promise<Vehicle[]> {
  const useGoogleDrive = await shouldUseGoogleDrive();
  if (useGoogleDrive && googleDriveRepository.isReady()) {
    return googleDriveRepository.getVehicles(profileId);
  }
  return [];
}

/**
 * Get all insurance policies from the appropriate storage
 */
export async function getInsurancePolicies(profileId: string): Promise<InsurancePolicy[]> {
  const useGoogleDrive = await shouldUseGoogleDrive();
  if (useGoogleDrive && googleDriveRepository.isReady()) {
    return googleDriveRepository.getInsurancePolicies(profileId);
  }
  return [];
}

export const storageRouterService = {
  // Address operations
  addAddress,
  updateAddress,
  deleteAddress,
  getAddresses,
  // Vehicle operations
  addVehicle,
  updateVehicle,
  deleteVehicle,
  getVehicles,
  // Insurance operations
  addInsurancePolicy,
  updateInsurancePolicy,
  deleteInsurancePolicy,
  getInsurancePolicies,
  // Health operations
  addHealthProvider,
  updateHealthProvider,
  deleteHealthProvider,
  // Financial operations
  addFinancialInstitution,
  updateFinancialInstitution,
  deleteFinancialInstitution,
  // Utility
  isUsingGoogleDrive,
  syncGoogleDrive,
};
